(() => {
  const STATUS_CLASS = 'zk-variant-status';
  const MEDIA_CLASS = 'zk-media-unavailable';

  function productRootForSection(sectionId) {
    const info = document.getElementById(`ProductInfo-${sectionId}`);
    return info ? info.closest('.product') : null;
  }

  function sectionIdForRoot(productRoot) {
    const info = productRoot?.querySelector('[id^="ProductInfo-"]');
    return info?.id?.replace('ProductInfo-', '') || '';
  }

  function getVariantSelects(productRoot) {
    return productRoot?.querySelector('variant-selects') || null;
  }

  function getColorGroup(productRoot) {
    if (!productRoot) return null;
    return Array.from(productRoot.querySelectorAll('variant-selects .product-form__input')).find((group) => {
      const label = group.querySelector('legend, label.form__label');
      const text = (label?.textContent || '').trim().toLowerCase();
      return text === 'color' || text.startsWith('color:') || text.startsWith('color ');
    }) || null;
  }

  function getSelectedColor(productRoot) {
    const colorGroup = getColorGroup(productRoot);
    if (!colorGroup) return '';
    const checked = colorGroup.querySelector('input[type="radio"]:checked');
    if (checked?.value) return checked.value.trim();
    const select = colorGroup.querySelector('select');
    if (select?.value) return select.value.trim();
    const selectedText = colorGroup.querySelector('[data-selected-value]');
    return (selectedText?.textContent || '').trim();
  }

  function selectedVariantFromRoot(productRoot) {
    const raw = productRoot?.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return null; }
  }

  function mediaVariantEntries(productRoot) {
    const script = productRoot?.querySelector('media-gallery [data-zk-media-variant-map]');
    if (!script) return [];
    try {
      const value = JSON.parse(script.textContent || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  }

  function uniqueVariantForMedia(productRoot, mediaId) {
    if (!mediaId) return null;
    const matches = mediaVariantEntries(productRoot).filter((entry) => entry?.mediaId === mediaId);
    if (!matches.length) return null;
    const byId = new Map(matches.map((entry) => [String(entry.variantId), entry]));
    const unique = Array.from(byId.values());
    if (unique.length === 1) return unique[0];

    // If several variants intentionally share one image, keep the current variant if it is one of them.
    const selected = selectedVariantFromRoot(productRoot);
    if (selected?.id != null && byId.has(String(selected.id))) return byId.get(String(selected.id));
    return null;
  }

  function activeMediaItem(productRoot) {
    return productRoot?.querySelector('media-gallery .product__media-item.is-active') || null;
  }

  function ensureStatus(productRoot) {
    if (!productRoot) return null;
    let status = productRoot.querySelector(`.${STATUS_CLASS}`);
    if (status) return status;
    const picker = getVariantSelects(productRoot);
    if (!picker) return null;
    status = document.createElement('p');
    status.className = STATUS_CLASS;
    status.hidden = true;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    picker.insertAdjacentElement('afterend', status);
    return status;
  }

  function clearMediaState(productRoot) {
    productRoot?.querySelectorAll(`.${MEDIA_CLASS}`).forEach((media) => {
      media.classList.remove(MEDIA_CLASS);
      media.removeAttribute('data-zk-unavailable-label');
    });
  }

  function unavailableStateForActiveMedia(productRoot) {
    const activeItem = activeMediaItem(productRoot);
    if (!activeItem) return null;

    const mapped = uniqueVariantForMedia(productRoot, activeItem.dataset.mediaId || '');
    if (mapped) return { unavailable: mapped.available === false, item: activeItem };

    // Non-variant lifestyle/detail images remain clean even when an unavailable variant is selected.
    return { unavailable: false, item: activeItem };
  }

  function syncMediaState(productRoot) {
    if (!productRoot) return;
    clearMediaState(productRoot);

    const state = unavailableStateForActiveMedia(productRoot);
    if (!state?.unavailable || !state.item) return;

    // Restrict the treatment to the visible media surface, never the slider/grid container.
    const mediaSurface = state.item.querySelector('.product__media') || state.item.querySelector('.product-media-container');
    if (!mediaSurface) return;
    mediaSurface.classList.add(MEDIA_CLASS);
    mediaSurface.setAttribute('data-zk-unavailable-label', productRoot.dataset.zkUnavailableLabel || 'Color no disponible');
  }

  function applyAvailability(sectionId, variant) {
    if (!sectionId) return;
    const productRoot = productRootForSection(sectionId);
    if (!productRoot) return;

    const status = ensureStatus(productRoot);
    const unavailable = !!variant && variant.available === false;
    const color = getSelectedColor(productRoot);
    const label = color ? 'Color no disponible' : 'Opción no disponible';

    productRoot.dataset.zkVariantUnavailable = unavailable ? 'true' : 'false';
    productRoot.dataset.zkUnavailableLabel = label;
    productRoot.classList.toggle('zk-selected-variant-unavailable', unavailable);

    if (status) {
      if (unavailable) {
        status.textContent = color
          ? `El color ${color} no está disponible en este momento.`
          : 'Esta opción no está disponible en este momento.';
        status.hidden = false;
      } else {
        status.textContent = '';
        status.hidden = true;
      }
    }

    window.requestAnimationFrame(() => syncMediaState(productRoot));
  }

  function currentMediaId(productRoot) {
    return activeMediaItem(productRoot)?.dataset?.mediaId || '';
  }

  function bindGallery(productRoot) {
    if (!productRoot || productRoot.dataset.zkAvailabilityGalleryBound === 'true') return;
    productRoot.dataset.zkAvailabilityGalleryBound = 'true';

    const gallery = productRoot.querySelector('media-gallery');
    const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
    if (!gallery) return;

    let syncFrame = 0;
    const scheduleMediaStateSync = () => {
      if (syncFrame) window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = 0;
        syncMediaState(productRoot);
      });
    };

    // Gallery browsing is visual only. It must never mutate Shopify's selected variant.
    // This prevents the former loop: gallery -> variant change -> Shopify resets gallery.
    gallery.addEventListener('click', () => {
      scheduleMediaStateSync();
      window.setTimeout(scheduleMediaStateSync, 80);
    });

    // Mobile swipe / slider navigation: only refresh the sold-out treatment for the viewed media.
    viewer?.addEventListener('slideChanged', scheduleMediaStateSync);

    // Shopify's media-gallery toggles .is-active internally. Observe that state so the
    // unavailable treatment follows arrows, thumbnails and swipes without changing variants.
    const observer = new MutationObserver((mutations) => {
      const activeChanged = mutations.some((mutation) =>
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class' &&
        mutation.target?.classList?.contains('product__media-item')
      );
      if (activeChanged) scheduleMediaStateSync();
    });

    observer.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Keep a reference for Shopify section reloads / defensive cleanup if needed later.
    productRoot._zkAvailabilityMediaObserver = observer;

    // Initial sync after binding.
    scheduleMediaStateSync();
  }

  function bootRoot(info) {
    if (!info) return;
    const sectionId = info.id.replace('ProductInfo-', '');
    const productRoot = info.closest('.product');
    bindGallery(productRoot);
    applyAvailability(sectionId, selectedVariantFromRoot(productRoot));
  }

  function boot() {
    document.querySelectorAll('[id^="ProductInfo-"]').forEach(bootRoot);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event?.data?.sectionId;
        const variant = event?.data?.variant;
        window.requestAnimationFrame(() => applyAvailability(sectionId, variant));
      });
    }
  }

  document.addEventListener('variant:change', (event) => {
    const productRoot = event.target?.closest?.('.product') || document.querySelector('.product');
    const sectionId = sectionIdForRoot(productRoot);
    window.requestAnimationFrame(() => applyAvailability(sectionId, event?.detail?.variant));
  });

  document.addEventListener('shopify:section:load', (event) => {
    const info = event.target?.querySelector?.('[id^="ProductInfo-"]');
    if (info) bootRoot(info);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
